import React, { useState, useEffect, useCallback } from 'react';
import { HabitTrackingModalProps } from './types';
import { DateSelector } from '../../DateSelector';
import { ProgressChart } from './components/ProgressChart/ProgressChart';
import styles from './HabitTrackingModal.module.scss';
import Button from '../../button/Button';
import { useApiClient } from '../../../api/ApiClientContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Icon } from '../../../assets/icons/Icon';
import { PositiveImpactsModal } from '../../PositiveImpactsModal';
import { useConflictSafeMutation } from '../../../hooks/useConflictSafeMutation';

export const HabitTrackingModal: React.FC<HabitTrackingModalProps> = ({
  isOpen,
  onClose,
  habitData,
  habitTitle = 'Use positive language',
  habitBehavior = '',
  habitDescription = '',
  programmeName = '',
  isActionBased = false,
  onLogSuccess,
  showPositiveImpacts = true,
  openPositiveImpactsOnMount = false,
  preselectedDate = null,
  }) => {
  const today = new Date();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const todayString = today.getFullYear() + '-' + 
    String(today.getMonth() + 1).padStart(2, '0') + '-' + 
    String(today.getDate()).padStart(2, '0');
  const [selectedDate, setSelectedDate] = React.useState<string | null>(preselectedDate || todayString);
  const [isPositiveImpactsModalOpen, setIsPositiveImpactsModalOpen] = useState(openPositiveImpactsOnMount);
  
  // Using useConflictSafeMutation to handle 409 conflicts automatically
  // If user state was updated in Teams, a 409 will redirect to the correct page
  const { mutate: logHabit, isPending } = useConflictSafeMutation({
    mutationFn: async (data: { log_date: string | null; log_type: string; positive_change_id?: string }) => {
      const res = await apiClient.api.logHabitApiV1HabitsUserHabitIdLogPost(habitData.id, data);
      return res?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['dashboardProgress'] });
      queryClient.invalidateQueries({ queryKey: ['habitById', habitData?.id] });
      queryClient.invalidateQueries({ queryKey: ['habits'] });
      queryClient.invalidateQueries({ queryKey: ['habitProgress'] });
      // For normal habits (not action-based) with showPositiveImpacts false, close modal after log
      if (!isActionBased && !showPositiveImpacts) {
        onClose();
      }
    },
  });

  const { data: habitById } = useQuery({
    queryKey: ['habitById', habitData?.id],
    queryFn: async () => {
      const res = await apiClient.api.getHabitProgressApiV1HabitsUserHabitIdProgressGet(
        habitData?.id,
      );
      return res?.data;
    },
  });
  const handleLogHabit = () => {
    // For action-based habits, don't send the log request yet
    // Just proceed to the next modal and send all requests at the end
    if (isActionBased && onLogSuccess) {
      onLogSuccess(selectedDate);
      return;
    }
    
    const payload = {
      log_date: selectedDate,
      log_type: 'completed',
    };
    logHabit(payload);
  };


  const handleAddImpactsClick = () => {
    //onClose(); 
    setIsPositiveImpactsModalOpen(true); // Open the positive impacts modal
  };

  const handleSelectImpact = (impactId: string) => {
    const payload = {
      log_date: selectedDate,
      log_type: 'completed',
      positive_change_id: impactId,
    };
    logHabit(payload);
    setIsPositiveImpactsModalOpen(false);
    handleModalClose(); // Close the main modal after saving
  };

  const handleModalClose = useCallback(() => {
    setSelectedDate(todayString);
    onClose();
  }, [todayString, onClose]);

  // Handle openPositiveImpactsOnMount and preselectedDate when modal opens
  useEffect(() => {
    if (isOpen) {
      if (openPositiveImpactsOnMount) {
        setIsPositiveImpactsModalOpen(true);
      }
      if (preselectedDate) {
        setSelectedDate(preselectedDate);
      }
    }
  }, [isOpen, openPositiveImpactsOnMount, preselectedDate]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      const modal = document.querySelector(`.${styles['habit-modal']}`);
      if (modal) {
        (modal as HTMLElement).focus();
      }
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleModalClose();
      }
    },
    [handleModalClose],
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);





  if (!isOpen) return null;

  // If opening only for positive impacts (from action completion), just show the PositiveImpactsModal
  if (openPositiveImpactsOnMount && isPositiveImpactsModalOpen) {
    return (
      <PositiveImpactsModal
        isOpen={isPositiveImpactsModalOpen}
        onClose={() => {
          setIsPositiveImpactsModalOpen(false);
          handleModalClose();
        }}
        positiveChanges={habitData?.habit?.positive_changes || []}
        onSelectImpact={handleSelectImpact}
        isLoading={isPending}
      />
    );
  }

  return (
    <div className={styles['habit-modal-overlay']} onClick={handleModalClose}>
      <div
        className={styles['habit-modal']}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        tabIndex={-1}
      >
        <div className={styles['habit-modal__header']}>
          <div className={styles['habit-modal__title-section']}>
            <h2 id="modal-title" className={styles['habit-modal__title']}>
              Track your habit
            </h2>
          </div>
          <button
            className={styles['habit-modal__close-button']}
            onClick={handleModalClose}
            aria-label="Close modal"
            type="button"
          >
            <Icon iconName="CrossIcon" width="1.2rem" height="1.2rem"/>
          </button>
        </div>

        <div className={styles['habit-modal__content']}>


          <div className={styles['habit-modal__details']}>
            <div className={styles['habit-modal__chart-section']}>
              <ProgressChart 
                completed={habitById?.days_completed || habitData.days_completed} 
                total={(habitById as any)?.min_completion_days || habitById?.total_days || (habitData.min_completion_days || habitData.days_completed + habitData.days_remaining)}
              />
            </div>

            <div className={styles['habit-modal__info-section']}>
              <div className={styles['habit-modal__habit-info']}>
                {programmeName && (
                  <span className={styles['habit-modal__badge']}>{programmeName}</span>
                )}
                <h3 className={styles['habit-modal__habit-title']}>{habitTitle}</h3>
                <p className={styles['habit-modal__habit-behavior']}>{habitBehavior}</p>
              </div>
              <div>
              <p className={styles['habit-modal__habit-description']}>{habitDescription}</p>
              <div className={styles.footerAction}>
              <h3>Have you done this?</h3>
              <div>
                <div className={styles['habit-modal__date-selector']}>
                  <DateSelector
                    selectedDate={selectedDate}
                    onDateChange={setSelectedDate}
                    placeholder="Select date"
                    recentLogs={habitById?.recent_logs || []}
                  />
                  <Button 
                    size="small" 
                    onClick={() => handleLogHabit()} 
                    disabled={isPending || habitById?.recent_logs?.some(log => log.log_date === selectedDate)}
                  >
                    {isPending ? 'Loading...' : 'Yes'}
                  </Button>
  
                </div>
                <div className={styles['habit-modal__date-info']}>You can log 3 days prior to today</div>
              </div>
            </div>
              </div>
            </div>
            
          </div>

        </div>

        <div className={styles.action}>
          {!isActionBased && showPositiveImpacts && habitById?.recent_logs?.some(log => log.log_date === selectedDate && !log.positive_change) && 
            <div className={styles.footerAction}>
            <>
              <div className={styles.footerTitle}>
            <div className={styles.footerTitleContent}>
                <h3>Well done! Have you noticed any positive impacts?</h3>
                <div className={styles.addImpactsBtn} onClick={handleAddImpactsClick}>
                <Icon iconName="PlusIcon" width="1.25rem" height="1.25rem" />
                Add impacts
                </div>
                </div>

                </div>
               
            </>
          </div>
    }
        </div>
      </div>
      
      <PositiveImpactsModal
        isOpen={isPositiveImpactsModalOpen}
        onClose={() => {
          setIsPositiveImpactsModalOpen(false);
          handleModalClose();
        }}
        positiveChanges={habitData?.habit?.positive_changes || []}
        onSelectImpact={handleSelectImpact}
        isLoading={isPending}
      />
    </div>
  );
};
